import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { LuShieldCheck } from "react-icons/lu";
import { cn } from "@/lib/utils";

export interface HomeAdminZoneProps {
  title?: string;
  description?: string;
  /** Pulses an "action needed" chip — use when the admin must do something now. */
  actionRequired?: boolean;
  children?: ReactNode;
  className?: string;
}

/**
 * Visually separates organizer-only controls from the member-facing Home card.
 * Amber accent + shield icon — deliberately *not* soccer-green so admins instantly
 * know this strip is private to them and expects a decision.
 */
export function HomeAdminZone({
  title,
  description,
  actionRequired = false,
  children,
  className,
}: HomeAdminZoneProps) {
  const { t } = useTranslation();

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-2xl border-2 border-amber-500/40",
        "bg-gradient-to-br from-amber-500/[0.1] via-amber-500/[0.04] to-transparent",
        className
      )}
    >
      <div className="space-y-3 p-4">
        <div className="flex items-start gap-2.5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-amber-500/35 bg-amber-500/15">
            <LuShieldCheck
              className="h-4 w-4 text-amber-700 dark:text-amber-300"
              aria-hidden
            />
          </div>
          <div className="min-w-0 flex-1 space-y-0.5">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <p className="text-sm font-bold tracking-tight text-amber-950 dark:text-amber-50">
                {title ?? t("v2.home.adminZoneTitle")}
              </p>
              <span className="rounded-md border border-amber-500/35 bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-800 dark:text-amber-200">
                {t("v2.home.adminOnly")}
              </span>
              {actionRequired && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-600/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-900 dark:text-amber-100">
                  <span className="relative flex h-1.5 w-1.5" aria-hidden>
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-500 opacity-75" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-amber-600" />
                  </span>
                  {t("v2.home.adminActionRequired")}
                </span>
              )}
            </div>
            {description && (
              <p className="text-sm leading-snug text-muted-foreground">{description}</p>
            )}
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}
